import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { INeighborhood, defaultValue } from 'app/shared/model/neighborhood.model';
import { sortBy } from 'lodash';
import _ from 'lodash';

const initialState: EntityState<INeighborhood> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
  menuOptions: [],
};

const apiUrl = 'api/neighborhoods';
const apiViewUrl = 'api/neighborhoods/view';

// Actions

export const getEntities = createAsyncThunk('neighborhood/fetch_entity_list', async ({ page, size, sort, filterUrl }: IQueryParams) => {
  const requestUrl = `${apiUrl}?${filterUrl}&cacheBuster=${new Date().getTime()}`;
  return axios.get<INeighborhood[]>(requestUrl);
});

export const getViewEntities = createAsyncThunk('neighborhood/fetch_entity_list', async ({ page, size, sort, filterUrl }: IQueryParams) => {
  const requestUrl = `${apiViewUrl}?${filterUrl}&cacheBuster=${new Date().getTime()}`;
  return axios.get<INeighborhood[]>(requestUrl);
});

export const getByCityEntities = createAsyncThunk('city/fetch_by_city_entity_list', async (city: string) => {
  const requestUrl = `${apiUrl}?${city ? `city.equals=${city}&size=10000000&` : ''}cacheBuster=${new Date().getTime()}`;
  return axios.get<INeighborhood[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'neighborhood/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<INeighborhood>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'neighborhood/create_entity',
  async (entity: INeighborhood, thunkAPI) => {
    const result = await axios.post<INeighborhood>(apiUrl, cleanEntity(entity));
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'neighborhood/update_entity',
  async (entity: INeighborhood, thunkAPI) => {
    const result = await axios.put<INeighborhood>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const partialUpdateEntity = createAsyncThunk(
  'neighborhood/partial_update_entity',
  async (entity: INeighborhood, thunkAPI) => {
    const result = await axios.patch<INeighborhood>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'neighborhood/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<INeighborhood>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const NeighborhoodSlice = createEntitySlice({
  name: 'neighborhood',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities, getViewEntities), (state, action) => {
        const { data, headers } = action.payload;
        return {
          ...state,
          loading: false,
          //  menuOptions: _.uniqBy(sortBy(data, ['name'])?.map(d => ({ label: d?.name, value: d.id })), 'label'),
          entities: data,
          menuOptions: data?.sort()?.map(d => {
            return { label: d.name, value: d.id };
          }),
          totalItems: parseInt(headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, getViewEntities), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = NeighborhoodSlice.actions;

// Reducer
export default NeighborhoodSlice.reducer;
